import { useChildScope } from "@/context/ChildScopeContext";
import { Label } from "@/components/ui/label";

// Phase 3 Delivery B — explicit child selection on create.
//
// Compact chip-row picker used in every "create" dialog (message,
// journal, document, request, event, expense). Intentionally small so
// it nests inside any form without redesigning the dialog.
//
// Behaviour:
//  - Hidden entirely if the household has zero children (graceful
//    onboarding — caller's submit can proceed with an empty list).
//  - In single-child scope (ChildSwitcher pinned to a child), the
//    picker auto-collapses into a read-only badge confirming the
//    pinned child is the target. The form's `value` should already be
//    [selectedChildId] at this point.
//  - In master "All children" mode, all children render as toggle
//    chips. Multi-select is allowed (one shared record with multiple
//    child_ids per the locked decision).
//  - `archived` children are excluded — you can't tag a new entry with
//    an archived child.
//
// Props:
//  - value:       string[]            currently selected child_ids
//  - onChange:    (ids) => void
//  - testPrefix:  string              data-testid prefix, e.g. "msg"
//  - label:       string              optional override for the label
//  - required:    boolean             whether to show the "Required"
//                                     marker (defaults to true)
//  - hint:        string              optional helper line beneath
//                                     (defaults to a sensible default)
export function ChildPicker({
  value = [],
  onChange,
  testPrefix = "child-picker",
  label = "Which child is this for?",
  required = true,
  hint,
}) {
  const { children, selectedChildId } = useChildScope();
  const active = (children || []).filter((c) => !c.archived_at);

  // Brand-new household: nothing to pick — let the caller's submit go
  // through with an empty list. Server-side `_enforce_create_child_ids`
  // accepts that path while no children exist.
  if (active.length === 0) return null;

  const selected = Array.isArray(value) ? value : [];
  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    onChange(next);
  };

  // Single-child scope: collapse the picker to a confirming badge.
  if (selectedChildId) {
    const child = active.find((c) => c.id === selectedChildId);
    if (!child) return null;
    return (
      <div className="space-y-1" data-testid={`${testPrefix}-pinned`}>
        <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">{label}</Label>
        <div
          className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-[#1F9FA4]/40 bg-[#E0F2F2] text-[#116B6E] text-xs"
          data-testid={`${testPrefix}-pinned-chip`}
        >
          <span
            className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-white/60"
            style={{ backgroundColor: child.color || "#1F9FA4" }}
          />
          <span className="font-medium">{child.name}</span>
          <span className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A]">Pinned by switcher</span>
        </div>
      </div>
    );
  }

  // Master view — full chip selector.
  const defaultHint = "Tap one or more. Pick the children this entry is about — it will only be visible under those filters.";
  return (
    <div className="space-y-1" data-testid={testPrefix}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">{label}</Label>
        {required && (
          <span className="text-[10px] uppercase tracking-[0.1em] text-[#825A41] font-normal">Required</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 rounded-sm border border-[#D9E1E3] bg-white p-2">
        {active.map((c) => {
          const on = selected.includes(c.id);
          return (
            <button
              type="button"
              key={c.id}
              onClick={() => toggle(c.id)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs transition-colors ${
                on
                  ? "bg-[#2E3A3F] border-[#2E3A3F] text-white"
                  : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"
              }`}
              data-testid={`${testPrefix}-${c.id}`}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full border border-black/10"
                style={{ backgroundColor: c.color || "#D9E1E3" }}
              />
              {c.name}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-[#6F828A]">
        {hint || defaultHint}
      </p>
    </div>
  );
}

// Validation helper used by callers — returns a friendly toast string
// when the form is in an invalid state ("master view + 0 selected"),
// or null when submission is allowed.
export function validateChildSelection({ value, hasChildren, selectedChildId }) {
  // Households with no children: server allows empty.
  if (!hasChildren) return null;
  // Single-child scope: caller should have pre-filled value.
  if (selectedChildId) return null;
  if (!Array.isArray(value) || value.length === 0) {
    return "Please pick at least one child for this entry.";
  }
  return null;
}
